import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslate } from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { apiClient } from '../../utils/apiClient';
import type { ApiError } from '../../utils/apiClient';
import { ReportBody } from './ReportBody';
import { HandoffActions } from './HandoffActions';
import { StatusChip, formatTimestamp, anchorTypeLabel } from './reportDisplay';
import type { InvestigationDetail } from './types';

interface InvestigationDetailDialogProps {
  investigationId: string | null;
  open: boolean;
  onClose: () => void;
  // Phase-2 injection host: sub-plan 18 (PromoteToPlaybookButton) injects into this stable
  // header-actions area. 14 owns the host markup; the downstream sub-plans own their injection
  // edits and 14 imports neither.
  headerActions?: ReactNode;
  // Phase-2 body injection host: sub-plan 20 (Model A/B replay + comparison panels) injects below
  // the Phase-1 report/handoff/steps blocks. Rendered only when `detail` has loaded, and the caller
  // is responsible for gating its content on the manage permission.
  bodyContent?: ReactNode;
}

// GET /investigations/:id → full report + persisted steps trace + HandoffActions.
// A 404 (AI_INVESTIGATION_NOT_FOUND — missing OR out-of-scope) surfaces as an in-dialog
// "not found / no access" message instead of crashing the list.
export const InvestigationDetailDialog = ({
  investigationId,
  open,
  onClose,
  headerActions,
  bodyContent,
}: InvestigationDetailDialogProps) => {
  const translate = useTranslate();
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<InvestigationDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!open || !investigationId) return;
    let cancelled = false;
    setLoading(true);
    setDetail(null);
    setError(null);
    setNotFound(false);

    apiClient
      .get<{ data: InvestigationDetail }>(
        `/api/backend/ai-troubleshooting/investigations/${investigationId}`,
      )
      .then((res) => {
        if (cancelled) return;
        setDetail(res.data);
      })
      .catch((err: ApiError) => {
        if (cancelled) return;
        if (err?.status === 404) {
          setNotFound(true);
        } else {
          setError(err?.message || translate('aiTroubleshooting.detail.loadFailed'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, investigationId, translate]);

  const showHandoff =
    detail && typeof detail.handoffMarkdown === 'string' && detail.handoffMarkdown.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { sm: 'center' },
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {translate('aiTroubleshooting.detail.title')}
            </Typography>
            {detail && <StatusChip translate={translate} status={detail.status} />}
          </Box>
          {/* Phase-2 injection host: stable header-actions area (sub-plans 18 / 20). */}
          {detail && headerActions ? (
            <Box
              data-testid="detail-header-actions"
              sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
            >
              {headerActions}
            </Box>
          ) : null}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {notFound && (
          <Alert severity="warning">{translate('aiTroubleshooting.detail.notFound')}</Alert>
        )}

        {error && !notFound && <Alert severity="error">{error}</Alert>}

        {detail && !loading && (
          <Box>
            {/* Meta */}
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary" component="div">
                {translate('aiTroubleshooting.detail.created')}: {formatTimestamp(detail.createdAt)}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                {translate('aiTroubleshooting.detail.anchor')}:{' '}
                {anchorTypeLabel(translate, detail.anchorType)}
                {detail.anchorId ? ` (${detail.anchorId})` : ''}
              </Typography>
            </Box>

            {/* Symptom */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1 }}>
              {translate('aiTroubleshooting.detail.symptom')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
              {detail.symptomText}
            </Typography>

            {/* Report (refusal | findings; nullable evidence/usage/latency guarded in ReportBody) */}
            <ReportBody data={detail} />

            {/* Handoff actions when present */}
            {showHandoff && (
              <HandoffActions
                markdown={detail.handoffMarkdown as string}
                investigationId={detail.id}
              />
            )}

            {/* Persisted steps trace — render the human text off `content`, NOT `summary`. */}
            {detail.steps && detail.steps.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 1 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {translate('aiTroubleshooting.detail.steps')}
                </Typography>
                <List dense disablePadding>
                  {detail.steps.map((step, idx) => (
                    <ListItem key={`${step.stepIndex}-${idx}`} sx={{ py: 0.25, alignItems: 'flex-start' }}>
                      <ListItemText
                        primary={step.content ?? ''}
                        secondary={
                          step.toolName
                            ? `${step.type} · ${step.toolName}`
                            : step.type
                        }
                        slotProps={{
                          primary: { sx: { fontSize: '0.875rem' } },
                          secondary: { sx: { fontSize: '0.7rem' } },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Phase-2 body injection host (sub-plan 20): Model A/B replay + comparison panels.
                The caller gates this content on the manage permission. */}
            {bodyContent}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{translate('aiTroubleshooting.detail.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvestigationDetailDialog;
